import { Component, Prop, h } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-platform-requirements';

/**
 * Platform requirements section listing legal or compliance criteria.
 * Grey background card with a full-width heading above a two-column body: left column carries text plus a check-icon list, right column carries supporting text.
 * Combines a checklist with side-by-side prose, distinguishing it from plain text overviews and grid-only key-feature variants.
 *
 * @category SignNow
 * @slot heading - {content} <me-heading variant="4" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Section heading for platform and device requirements for {keyword}. 6-10 words.
 * @slot leftText - {content} <me-text> - Introductory paragraph covering the browser and OS environment needed to use {keyword}. Mention TLS version, supported browsers, and mobile app availability. You MUST write at least 35 words.
 * @slot rightText - {content} <me-text> - Closing paragraph summarising enterprise and regulated deployment requirements for {keyword}: managed devices, API access, SSO provisioning, and certificate or LTV configuration. You MUST write at least 45 words.
 * @example
 *   <sn-platform-requirements
 *     items='[{"label":"ESIGN/UETA:","text":"Supports legal enforceability"},{"label":"Audit certificate:","text":"Documented signing history"}]'
 *   >
 *     <me-heading slot="heading" variant="4" tag="h2">Legal validity of eSignatures</me-heading>
 *     <me-text slot="leftText">Key requirements for legally valid electronic signatures:</me-text>
 *     <me-text slot="rightText">Additional considerations for compliance and legal validity.</me-text>
 *   </sn-platform-requirements>
 */
@Component({
  tag: 'sn-platform-requirements',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DpcRequirements {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Left column checklist items. Each item can be a plain string or an object
   * with a bold `label` and a `text` description.
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} label - Short label for this platform or device requirement category (e.g. Browser support, Mobile apps). 1-4 words.
   * @item.property {string} text - Specific requirement detail: list supported versions, OS names, or technical specifications relevant to {keyword}. 12-20 words.
   * @item.required text
   * @item.content label text
   * @minItems 3
   * @maxItems 3
   */
  @Prop({ mutable: true, attribute: 'items' }) leftItems?: (string | { label?: string; text: string })[] | string;

  /**
   * Legacy / raw-HTML fallback. For new usage prefer slots + `leftItems` prop.
   * @dynamic
   * @type {object}
   * @property {string} raw_html - Raw HTML content (replaces entire block)
   * @property {string} title - Fallback heading (use slot="heading" instead)
   * @property {string} left_text - Left column text (use slot="leftText" instead)
   * @property {array} left_items - Checklist items (use leftItems prop instead)
   * @property {string} right_text - Right column text (use slot="rightText" instead)
   */
  @Prop({ mutable: true }) values?:
    | {
        title?: string;
        left_text?: string;
        left_items?: string[];
        right_text?: string;
        raw_html?: string;
      }
    | string;

  componentWillLoad() {
    this.values = parseJsonProp(this.values);
    this.leftItems = parseJsonProp(this.leftItems);
  }

  render() {
    const values = typeof this.values === 'object' ? this.values : undefined;

    if (values?.raw_html) {
      return <me-section class={COMPONENT_NAME} yGap="md" yGapLg="xl" innerHTML={values.raw_html}></me-section>;
    }

    const leftItems = Array.isArray(this.leftItems)
      ? this.leftItems
      : (Array.isArray(values?.left_items) ? values.left_items : []);

    return (
      <me-section class={COMPONENT_NAME} yGap="3xl" yGapLg="5xl">
        <div class={`${COMPONENT_NAME}__wrapper`}>
          <me-row direction="column" directionLg="column" yGap="xs" yGapLg="3xl">
            <me-column spanLg="12" class={`${COMPONENT_NAME}__title`}>
              <slot name="heading"></slot>
            </me-column>
            <me-column spanLg="12">
              <me-row direction="column" directionLg="row" yGap="md" yGapLg="none" xGapLg="2xl">
                <me-column spanLg="6">
                  <div class={`${COMPONENT_NAME}__left`}>
                    <slot name="leftText"></slot>
                    {leftItems.length > 0 && (
                      <ul class={`${COMPONENT_NAME}__list`}>
                        {leftItems.map((item) => {
                          const isObj = typeof item === 'object' && item !== null;
                          return (
                            <li class={`${COMPONENT_NAME}__item`}>
                              <me-icon name="interface--check" class={`${COMPONENT_NAME}__check`} width="24" height="24" lazy={false}></me-icon>
                              <me-text tag="span">
                                {isObj ? ([
                                  item.label && <strong>{item.label}</strong>,
                                  item.label ? ` ${item.text}` : item.text,
                                ]) : (
                                  item as string
                                )}
                              </me-text>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </me-column>
                <me-column spanLg="6">
                  <div class={`${COMPONENT_NAME}__right`}>
                    <slot name="rightText"></slot>
                  </div>
                </me-column>
              </me-row>
            </me-column>
          </me-row>
        </div>
      </me-section>
    );
  }
}
